import { clsx } from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { LabeledChoiceElement } from '@/internal/mixin/labeledChoiceElement'

import { CommonFormElementSize, CommonSize } from '../../common/types'
import styles from './switch.scss?inline'

/**
 * `dap-ds-switch`
 * @summary A switch is a graphical control element that allows the user to toggle between two states.
 * @element dap-ds-switch
 * @title - Switch
 *
 * @property {string} name - The name of the switch.
 * @property {string} value - The value of the switch.
 * @property {string} label - The label of the switch.
 * @property {string} description - The description of the switch.
 * @property {boolean} checked - The checked state of the switch.
 * @property {'xs' | 'sm' | 'md' | 'lg'} size - The size of the switch.
 * @property {boolean} disabled - The disabled state of the switch.
 * @property {boolean} required - The required state of the switch.
 * @property {'left' | 'right'} labelPlacement - The placement of the label.
 * @property {'top' | 'bottom'} descriptionPlacement - The placement of the description.
 * @property {boolean} subtle - The weight of the label.
 * @property {string} feedback - The feedback of the switch.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the switch.
 * @property {boolean} optional - The optional state of the switch.
 * @property {string} optionalLabel - The optional label of the switch.
 *
 * @cssProperty --dds-switch-width - Width of the switch control.
 * @cssProperty --dds-switch-height - Height of the switch control.
 * @cssProperty --dds-switch-thumb-width - Width of the switch thumb.
 * @cssProperty --dds-switch-thumb-height - Height of the switch thumb.
 * @cssProperty --dds-switch-thumb-margin - Margin around the switch thumb.
 * @cssProperty --dds-switch-thumb-color - Color of the switch thumb.
 * @cssProperty --dds-switch-unchecked-bg - Background color of the switch when unchecked.
 * @cssProperty --dds-switch-checked-bg - Background color of the switch when checked.
 * @cssProperty --dds-switch-invalid-bg - Background color of the switch when invalid.
 * @cssProperty --dds-switch-disabled-bg - Background color of the switch when disabled.
 * @cssProperty --dds-switch-wrapper-padding - Padding of the switch wrapper.
 * @cssProperty --dds-switch-wrapper-bg - Background color of the switch wrapper.
 * @cssProperty --dds-switch-border-color - Border color of the switch.
 * @cssProperty --dds-switch-high-contrast-border-color - Border color of the switch in high contrast mode.
 *
 * @event {{ value: string }} dds-change - Fired when the input value changes.
 * @event {{ void }} dds-blur - Emitted when the input loses focus.
 * @event {{ void }} dds-focus - Emitted when the input gains focus.
 * @event {{ value: string }} dds-input - Emitted when the input receives input.
 *
 * @csspart base - The main switch container.
 * @csspart baselabel - The main label container
 * @csspart label - The label of the switch.
 * @csspart input - The native input of the switch.
 * @csspart control - The control of the switch.
 * @csspart description - The description of the switch.
 */
@customElement('dap-ds-switch')
export default class DapDSSwitch extends LabeledChoiceElement {
  @query('#input') private readonly input!: HTMLInputElement

  /** This sets up border around the switch, when true. */
  @property({ type: Boolean }) border: boolean = false

  /** The type of the switch
   * @type {'normal' | 'background'}
   */
  @property({ type: String }) type: 'normal' | 'background' = 'normal'

  static readonly styles = unsafeCSS(styles)

  /**
   * Validates property combinations and logs warnings for invalid states.
   * @private
   */
  private validateProperties(): void {
    // Add any switch-specific validation here
    if (this.required && !this.checked) {
      // This will be handled by form validation, but we can provide early feedback
    }
  }

  /**
   * Gets the aria-describedby attribute value by combining description and feedback IDs.
   * @returns {string | undefined} Space-separated IDs or undefined if none exist
   * @private
   */
  private getAriaDescribedBy(): string | undefined {
    const ids: string[] = []

    if (this.description) {
      ids.push('description')
    }

    if (this.feedback) {
      ids.push('feedback')
    }

    return ids.length > 0 ? ids.join(' ') : undefined
  }

  /**
   * Handles click events on the switch.
   * Manages the checked state transitions and emits change events.
   * @private
   */
  private readonly handleClick = (): void => {
    // Early return if component is disabled
    if (this.disabled) {
      return
    }

    // Validate property combinations before state change
    this.validateProperties()

    // Toggle the checked state
    this.checked = !this.checked

    const { checked, value, disabled } = this

    // Emit change event with error handling
    try {
      const event = new CustomEvent('dds-change', {
        bubbles: true,
        cancelable: false,
        composed: true,
        detail: { checked, value: value || '', disabled },
      })

      this.dispatchEvent(event)
    } catch (error) {
      console.error('Error emitting switch change event:', error)
    }

    // Update validity state with error handling
    try {
      this.setValidity()
    } catch (error) {
      console.error('Error setting switch validity:', error)
    }
  }

  public get focusElement(): HTMLInputElement {
    if (!this.input) {
      throw new Error(
        'Switch input element not found. Component may not be properly initialized.',
      )
    }
    return this.input
  }

  /**
   * Programmatically focuses the switch input element.
   * @public
   */
  public focus(): void {
    if (this.input && !this.disabled) {
      this.input.focus()
    }
  }

  /**
   * Programmatically blurs the switch input element.
   * @public
   */
  public blur(): void {
    if (this.input) {
      this.input.blur()
    }
  }

  /**
   * Handles invalid events on the switch input.
   * Prevents the default browser validation UI from showing and manages invalid state.
   * @param {Event} event - The invalid event
   * @private
   */
  public handleInvalid(event: Event): void {
    event.preventDefault()
    // Set invalid state if not already set
    if (!this.invalid) {
      this.invalid = true
    }
  }

  /**
   * Handles keyboard events on the switch.
   * Provides enhanced keyboard navigation and accessibility.
   * @param {KeyboardEvent} event - The keyboard event
   * @private
   */
  private handleKeyDown(event: KeyboardEvent): void {
    // Handle Space key to toggle switch (in addition to default behavior)
    if (event.code === 'Space') {
      // Allow default Space behavior for switch input
      // This ensures proper accessibility and form behavior
      return
    }

    // Handle Enter key to toggle switch (custom behavior)
    if (event.code === 'Enter') {
      event.preventDefault()
      this.handleClick()
    }
  }

  render() {
    return html`
      <div part="base">
        <label
          part="baselabel"
          id="label"
          class=${clsx('switch choice', `switch--${this.size}`, {
            'switch--checked': this.checked,
            'switch--disabled': this.disabled,
            'switch--required': this.required,

            'choice__label--right': this.labelPlacement === 'right',
            'choice__label--left': this.labelPlacement === 'left',
          })}>
          <div
            class=${clsx('input__wrapper', {
              'input__wrapper--background': this.type === 'background',
              'input__wrapper--border': this.border,
            })}>
            <input
              part="input"
              type="checkbox"
              class="input"
              id="input"
              role="switch"
              .checked=${this.checked}
              .required=${this.required}
              .disabled=${this.disabled}
              aria-label=${ifDefined(this.ariaLabel) || ''}
              aria-describedby=${ifDefined(this.getAriaDescribedBy())}
              aria-checked=${this.checked ? 'true' : 'false'}
              .aria-invalid=${this.invalid ? 'true' : null}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick}
              @keydown=${this.handleKeyDown}
              @focus=${this.onFocus}
              @blur=${this.onBlur}
              @input=${this.onInput} />
            <span
              part="control"
              class=${clsx(
                'input__control',
                { 'input__control--disabled': this.disabled },
                { 'input__control--invalid': this.invalid },
                { 'input__control--checked': this.checked },
              )}>
              <span class="input__control-toggle">
                <dap-ds-icon
                  name="checkbox-blank-circle-fill"
                  class="input__control-toggle-thumb"
                  aria-hidden="true"></dap-ds-icon
                ><dap-ds-icon
                  name="checkbox-circle-fill"
                  class=${clsx(
                    'input__control-toggle-thumb',
                    'check-indicator',
                  )}
                  aria-hidden="true"></dap-ds-icon>
              </span>
              <span
                part="high-contrast"
                aria-hidden="true"
                class=${clsx(
                  'input__control-high-contrast',
                  `input__control-high-contrast--${this.size}`,
                  {
                    'input__control-high-contrast--disabled': this.disabled,
                  },
                )}></span>
            </span>
            <div
              part="label-container"
              class=${clsx('choice__label-container', {
                'choice__label-container--disabled': this.disabled,
                'choice__label--bottom': this.descriptionPlacement === 'top',
                'choice__label--top': this.descriptionPlacement === 'bottom',
              })}>
              ${this.label &&
              html`<dap-ds-form-label
                id="label"
                class="label"
                part="label"
                .size=${this.size}
                .required=${this.required}
                .optional=${this.optional}
                .optionalLabel=${this.optionalLabel}
                subtle
                .disabled=${this.disabled}
                label=${ifDefined(this.label)}
                renderAs="span">
              </dap-ds-form-label>`}
              ${this.description &&
              html`
                <dap-ds-typography
                  part="description"
                  class="description"
                  variant="description"
                  size=${ifDefined(this.size as CommonFormElementSize)}
                  id="description">
                  ${this.description}
                </dap-ds-typography>
              `}
            </div>
          </div>
          ${this.feedback &&
          html`
            <dap-ds-feedback
              part="feedback"
              feedbackType=${ifDefined(this.feedbackType)}
              feedbackSize=${ifDefined(this.size) as CommonSize}
              id="feedback"
              .subtle=${this.subtle}>
              ${this.feedback}
            </dap-ds-feedback>
          `}
        </label>
      </div>
    `
  }
}
